import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useState } from "react";
import { useRejectBillingBill } from "../../hooks/useRejectBillingBill";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { rejectBillSchema, RejectBillValidationSchema } from "../../schemas/rejectBill.schema";
import TextArea from "@/common/components/TextArea";

interface BillRejectModalProps {
  billId?: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const BillRejectModal = ({ billId, isOpen, setIsOpen }: BillRejectModalProps) => {
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RejectBillValidationSchema>({
    resolver: zodResolver(rejectBillSchema),
  });

  const {
    mutate: rejectBill,
    isPending: rejectBillIsPending,
  } = useRejectBillingBill({
    billId,
    onSuccess: () => {
      setIsOpen(false);
      queryClient.invalidateQueries({ queryKey: ['billingBill', billId] });
    },
    onError: (response) => {
      setIsOpen(false);
      setMessages(response.response?.data.errors.base || ['Ocorreu um erro ao rejeitar fatura'])
    },
  });

  const onSubmit: SubmitHandler<RejectBillValidationSchema> = (data) => rejectBill(data);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        useCancellableFooter
        showClose={false}
        confirmButtonIsLoading={rejectBillIsPending}
        dataTestid="confirm_remove_failure"
        onConfirm={handleSubmit(onSubmit)}
        title="Rejeitar fatura"
        subtitle="Preencha o motivo para rejeitar a fatura."
        titleClass="text-2xl font-bold mb-2"
        body={
          <form className="flex flex-col gap-4">
            <TextArea
              label="Justificativa"
              className="text-white-800"
              placeholder="Escreva aqui sua justificativa"
              {...register('reject_reason')}
              errorMessage={errors.reject_reason?.message as string || undefined}
              markAsRequired
            />
          </form>
        }
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
}

export default BillRejectModal;
